import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import moment from 'moment';
import { generateUuid } from '../components/Calendar/Events/EventsUtils';
import { providerCreateNewEvent, providerUpdateEvent, providerDeleteEvent } from '../components/Context/CalendarProvider';
import useGoogleSyncStore from './useGoogleSyncStore';
import useAuthStore from './useAuthStore';

// Helper to check if event is all day
// All-day events can end at either:
// - Start of next day (00:00) - Google Calendar style
// - End of same day (23:59:59) - local creation style
const isAllDay = ({ start, end }) => {
    const startMoment = moment(start);
    const endMoment = moment(end);

    // Start must be at beginning of day
    const startIsStartOfDay = startMoment.hours() === 0 && startMoment.minutes() === 0;

    // End can be at midnight (next day) OR at 23:59 (end of day)
    const endIsEndOfDay = (
        (endMoment.hours() === 0 && endMoment.minutes() === 0) ||
        (endMoment.hours() === 23 && endMoment.minutes() === 59)
    );

    return startIsStartOfDay && endIsEndOfDay;
};

// Helper to convert date strings back to Date objects when loading from storage
const hydrateEvents = (events) => {
    if (!events) return [];
    return events.map(event => ({
        ...event,
        start: event.start ? new Date(event.start) : null,
        end: event.end ? new Date(event.end) : null,
    }));
};

export const useEventsStore = create(
    persist(
        (set, get) => ({
            events: [],

            // Set all events (supports both direct value and callback pattern)
            setEvents: (eventsOrUpdater) => {
                if (typeof eventsOrUpdater === 'function') {
                    // Callback pattern: setEvents(prev => [...prev, newEvent])
                    set((state) => ({ events: eventsOrUpdater(state.events) }));
                } else {
                    // Direct value: setEvents([...])
                    set({ events: eventsOrUpdater });
                }
            },

    // Create a new event object
    createNewEvent: (calendar, start, end, copiedEvent = {}, useExistingID = false) => {
        const user = useAuthStore.getState().currentUser;
        return {
            ...copiedEvent,
            id: useExistingID ? copiedEvent.id : generateUuid(),
            start,
            end,
            // Preserve isAllDay if explicitly set in copiedEvent, otherwise calculate from times
            isAllDay: copiedEvent.isAllDay !== undefined ? copiedEvent.isAllDay : isAllDay({ start, end }),
            isDraggable: calendar.isWritable !== false,
            isWritable: calendar.isWritable !== false,
            visible: calendar.selected !== false,
            primary_email: calendar.primary_email,
            calendar: calendar.id,
            calendarColor: calendar.backgroundColor || null,
            calendarTextcolor: calendar.foregroundColor || null,
            // ── Sharing fields (defaults) ──
            visibility: copiedEvent.visibility || 'private',
            slug: copiedEvent.slug || null,
            inviteToken: copiedEvent.inviteToken || null,
            hostId: copiedEvent.hostId || user?.uid || null,
            hostDisplayName: copiedEvent.hostDisplayName || user?.displayName || null,
            hostPhotoURL: copiedEvent.hostPhotoURL || user?.photoURL || null,
            hostEmail: copiedEvent.hostEmail || user?.email || null,
            rsvpEnabled: copiedEvent.rsvpEnabled ?? false,
            maxAttendees: copiedEvent.maxAttendees ?? null,
            shareCardStyle: copiedEvent.shareCardStyle || null,
            invitedEmails: Array.isArray(copiedEvent.invitedEmails) ? copiedEvent.invitedEmails : [],
            createdAt: copiedEvent.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        };
    },

    // Add event
    addEvent: async (calendar, event, onEventAdded = null) => {
        const eventCopy = {
            ...event,
            id: event.id || generateUuid()
        };

        // Optimistically add to state
        set((state) => ({
            events: [...state.events, eventCopy]
        }));

        // Queue for sync (GRO-21)
        const syncStore = useGoogleSyncStore.getState();
        syncStore.addPendingChange('create', eventCopy);

        // Sync with provider
        try {
            const responseEvent = await providerCreateNewEvent(eventCopy, calendar.id);
            if (!responseEvent.error) {
                // Update with server response (may have different ID)
                set((state) => ({
                    events: state.events.map(e =>
                        e.id === eventCopy.id ? responseEvent : e
                    )
                }));

                // Publish newly created shared events only after we have the provider-backed ID.
                import('../services/sharing.service').then(({ syncSharedEventForEvent }) => {
                    syncSharedEventForEvent(responseEvent).catch(() => {});
                }).catch(() => {});

                // Track sync metadata (GRO-21)
                syncStore.setEventSyncMeta(responseEvent.id, {
                    source: 'local',
                    etag: responseEvent.etag
                });
                // Remove from pending since it's now synced
                const pendingChanges = syncStore.getPendingChangesForEvent(eventCopy.id);
                pendingChanges.forEach(c => syncStore.removePendingChange(c.id));

                if (onEventAdded) {
                    onEventAdded(responseEvent);
                }
                return responseEvent;
            }
        } catch (error) {
            console.error('Failed to sync event with provider');
        }
        return eventCopy;
    },

    // Update event
    updateEvent: (event) => {
        if (!event.id) {
            console.error('updateEvent: event missing id');
            return;
        }

        const events = get().events;
        const existingEvent = events.find(e => e.id === event.id);
        const updatedEvent = {
            ...(existingEvent || {}),
            ...event,
            isAllDay: isAllDay({ start: moment(event.start || existingEvent?.start), end: moment(event.end || existingEvent?.end) }),
            lastModified: new Date().toISOString(), // Track modification time (GRO-21)
            updatedAt: new Date().toISOString(),
        };

        set((state) => ({
            events: state.events.map(e =>
                e.id === event.id ? updatedEvent : e
            )
        }));

        // Keep shared_events in sync for social sharing pages.
        import('../services/sharing.service').then(({ syncSharedEventForEvent }) => {
            syncSharedEventForEvent(updatedEvent).catch(() => {});
        }).catch(() => {});

        // Queue for sync (GRO-21)
        const syncStore = useGoogleSyncStore.getState();
        syncStore.addPendingChange('update', updatedEvent);

        // Sync with provider
        providerUpdateEvent(updatedEvent).then(() => {
            // Remove from pending since it's now synced
            const pendingChanges = syncStore.getPendingChangesForEvent(event.id);
            pendingChanges.forEach(c => syncStore.removePendingChange(c.id));
            // Update sync metadata
            syncStore.setEventSyncMeta(event.id, {
                source: 'local',
                lastModified: updatedEvent.lastModified
            });
        });
    },

    // Remove event
    removeEvent: (event) => {
        // Queue for sync before removing (GRO-21)
        const syncStore = useGoogleSyncStore.getState();
        syncStore.addPendingChange('delete', event);

        // Remove any shared/public representation so stale links are invalidated.
        import('../services/sharing.service').then(({ unpublishEvent }) => {
            if (event?.id) {
                unpublishEvent(event.id).catch(() => {});
            }
        }).catch(() => {});

        set((state) => ({
            events: state.events.filter(e => e.id !== event.id)
        }));

        // Sync with provider
        providerDeleteEvent(event).then(() => {
            // Remove from pending since it's now synced
            const pendingChanges = syncStore.getPendingChangesForEvent(event.id);
            pendingChanges.forEach(c => syncStore.removePendingChange(c.id));
            // Remove sync metadata
            syncStore.removeEventSyncMeta(event.id);
        });
    },

    // Check/uncheck event
    checkEvent: (eventId) => {
        get().updateEvent({ id: eventId, checked: true });
    },

    uncheckEvent: (eventId) => {
        get().updateEvent({ id: eventId, checked: false });
    },

    // Check or uncheck event by task ID
    checkOrUncheckEventByTaskId: (taskId, checked) => {
        const events = get().events;
        const existingEvent = events.find(e => e.taskid === taskId);
        if (!existingEvent) return undefined;

        const updatedEvent = { ...existingEvent, checked };
        get().updateEvent(updatedEvent);
        return updatedEvent;
    },

    // Toggle calendar events visibility
    toggleCalendarEventsVisibility: (accountEmail, calId, isVisible) => {
        set((state) => ({
            events: state.events.map(event =>
                event.primary_email === accountEmail && event.calendar === calId
                    ? { ...event, visible: isVisible }
                    : event
            )
        }));
    },

    // Add or update events from provider calendar (supports incremental sync)
    addEventsFromProviderCalendar: (account, cal, newEvents, convertProviderEvent) => {
        set((state) => {
            // Build a map of current events by ID for efficient lookup
            const currentEventsMap = new Map(state.events.map(e => [e.id, e]));

            // Track events to add and IDs we've processed in this batch
            const eventsToAdd = [];
            const eventsToUpdate = new Map();
            const processedIds = new Set();

            for (const event of newEvents) {
                // Skip if already processed in this batch
                if (processedIds.has(event.id)) {
                    continue;
                }
                processedIds.add(event.id);

                const converted = convertProviderEvent(event);
                const newEvent = get().createNewEvent(cal, converted.start, converted.end, converted, true);

                if (currentEventsMap.has(event.id)) {
                    // Event exists - update it
                    eventsToUpdate.set(event.id, newEvent);
                } else {
                    // New event - add it
                    eventsToAdd.push(newEvent);
                }
            }

            // Apply updates to existing events and add new ones
            const updatedEvents = state.events.map(e =>
                eventsToUpdate.has(e.id) ? eventsToUpdate.get(e.id) : e
            );

            return {
                events: [...updatedEvents, ...eventsToAdd]
            };
        });
    },

    // Get event by ID
    getEventById: (id) => get().events.find(e => e.id === id),

    // Get events by calendar
    getEventsByCalendar: (calendarId) =>
        get().events.filter(e => e.calendar === calendarId),

    // Clear all events
    clearEvents: () => set({ events: [] }),
        }),
        {
            name: 'events-storage',
            // Custom storage to handle Date objects
            storage: {
                getItem: (name) => {
                    const str = localStorage.getItem(name);
                    if (!str) return null;
                    const parsed = JSON.parse(str);
                    // Hydrate events to convert date strings back to Date objects
                    if (parsed.state?.events) {
                        parsed.state.events = hydrateEvents(parsed.state.events);
                    }
                    return parsed;
                },
                setItem: (name, value) => {
                    localStorage.setItem(name, JSON.stringify(value));
                },
                removeItem: (name) => {
                    localStorage.removeItem(name);
                },
            },
            // Only persist events
            partialize: (state) => ({
                events: state.events,
            }),
        }
    )
);
